import { useState, useEffect, useCallback } from 'react';
import { useToast } from '../../Utils/toastContext';
import { setAvailableRoleList } from '../../Redux/store/userSlice';
import { useSelector, useDispatch } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { debounce } from 'lodash';
import { ShieldCheck, UserRound, UserRoundX, ShieldAlert } from 'lucide-react';

/**
 * Role Permissions screen
 * -----------------------
 * Self-service screen where the logged-in user can ASSIGN or REMOVE
 * roles on their own account (e.g. Staff Member / Compliance Officer).
 *
 * Endpoints used (same pattern as ComplianceOfficer.js self-grant call):
 *   ASSIGN -> POST /api/v1/agency/roles/self-grant   { role_id }
 *   REMOVE -> POST /api/v1/agency/roles/self-revoke  { role_id }
 *   (If your backend revoke is a DELETE, just change the method inside
 *    postRoleChangeAPI — everything else stays the same.)
 *
 * Keep ROLE_DEFINITIONS ids in sync with your backend:
 *   role_id 6 = Compliance Officer (same as your existing self-grant call)
 *   role_id 7 = Staff Member
 *
 * Assignment state is tracked by role ID (assignedRoleIds), synced from
 * redux on load and updated immediately when assign/remove succeeds.
 */

const ROLE_DEFINITIONS = [
  {
    id: 7, // ← change to your backend role id for Staff Member
    name: '',
    label: 'Staff Member',
    description:
      'Standard team member access. Can work on day-to-day operations, manage customer records, and issue their certificates.',
    permissions: [
      'Manage customers',
      'Review applications',
      'Issue certificates',
    ],
    icon: 'user',
    iconClass: 'bg-[#EEF2FF] text-[#3730A3] ring-1 ring-[#E0E7FF]',
  },
  {
    id: 6, // 6 for compliance (matches your existing self-grant call)
    name: '',
    label: 'Compliance Officer',
    description:
      'Advanced compliance access. Can review submissions, approve or send them back for resubmit, manage compliance records, and manage risk profiles.',
    permissions: [
      'Manage applications',
      'Compliance reporting',
      'Manage risk profiles',
    ],
    icon: 'shield',
    iconClass: 'bg-[#F0FDF4] text-[#166534] ring-1 ring-[#BBF7D0]',
  },
];

const RolePermission = () => {
  const { showToast } = useToast();
  const BASE_URL = process.env.REACT_APP_BASE_URL;
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const userId = useSelector((state) => state.user.userId);

  // The logged-in user's current roles (same redux slice you already use)
  const myRoles = useSelector((state) => state.user.availableRoles) || [];

  const [loading, setLoading] = useState(false);
  const [filter, setFilter] = useState('all'); // all | assigned | available
  const [searchQuery, setSearchQuery] = useState('');
  const [showRemoveModal, setShowRemoveModal] = useState(false);
  const [roleToRemove, setRoleToRemove] = useState(null);

  // ✅ Local assignment state — ids of roles currently assigned to me.
  // Tracked by ID (names can be empty), synced from redux and updated
  // immediately when assign/remove succeeds.
  const [assignedRoleIds, setAssignedRoleIds] = useState([]);

  // Sync local state from redux whenever the user's roles change
  // (initial login load, or after a grant/revoke dispatch)
  useEffect(() => {
    if (Array.isArray(myRoles) && myRoles.length > 0) {
      setAssignedRoleIds(myRoles.map((r) => r?.id).filter(Boolean));
    } else {
      setAssignedRoleIds([]);
    }
  }, [myRoles]);

  // ✅ Assigned check by role id (with name fallback in case names get filled in)
  const isAssigned = (def) =>
    assignedRoleIds.includes(def.id) ||
    myRoles.some((r) => def.name && r?.name === def.name);

  const prettyRoleName = (name = '') =>
    name.replace(/_/g, ' ').replace(/\b\w/g, (char) => char.toUpperCase());

  // Show a friendly label in the "Your roles" badges — use ROLE_DEFINITIONS
  // label when the id matches, otherwise fall back to the redux role name
  const roleBadgeLabel = (role) => {
    const def = ROLE_DEFINITIONS.find(
      (d) => d.id === role?.id || (d.name && d.name === role?.name),
    );
    return def ? def.label : prettyRoleName(role?.name) || `Role #${role?.id}`;
  };

  /* ----------------------------- API helpers ----------------------------- */

  const postRoleChangeAPI = async (roleId) => {
    const token = localStorage.getItem('jwtToken');
    const headers = {
      'Content-Type': 'application/json',
    };

    if (token) {
      headers.Authorization = `Bearer ${token}`;
    }

    const apiUrl = `${BASE_URL}/api/v1/agency/roles/self-grant`; // ← adjust if your revoke route differs

    const bodyData = {
      role_id: roleId,
    };

    try {
      const response = await fetch(apiUrl, {
        method: 'POST', // use 'DELETE' here if your revoke endpoint is a DELETE
        headers: headers,
        body: JSON.stringify(bodyData),
      });

      if (!response.ok) {
        if (response.status === 401) {
          localStorage.removeItem('jwtToken');
          navigate('/login');
          throw new Error('Session expired. Please log in again.');
        }
        const errorData = await response.json().catch(() => ({
          message: 'An unexpected error occurred',
        }));

        throw new Error(errorData.message || 'Something went wrong');
      }

      return await response.json();
    } catch (error) {
      throw new Error(error.message || 'Network error, please try again later');
    }
  };

  /* --------------------------- Assign / Remove --------------------------- */
  const updateApplication = async (roleId) => {
    const token = localStorage.getItem('jwtToken');
    const headers = {
      'Content-Type': 'application/json',
    };

    if (token) {
      headers.Authorization = `Bearer ${token}`;
    }
    const apiUrl =
      roleId == 7
        ? `${BASE_URL}/api/v1/agency/roles/self-grant/agent`
        : `${BASE_URL}/api/v1/agency/roles/self-grant/compliance`;

    const bodyData = {};
    try {
      const response = await fetch(apiUrl, {
        method: 'DELETE',
        headers: headers,
        body: JSON.stringify(bodyData),
      });

      if (!response.ok) {
        if (response.status === 401) {
          localStorage.removeItem('jwtToken');
          navigate('/login');
          throw new Error('Session expired. Please log in again.');
        }
        const errorData = await response.json().catch(() => ({
          message: 'An unexpected error occurred',
        }));

        throw new Error(errorData.message || 'Something went wrong');
      }

      return await response.json();
    } catch (error) {
      throw new Error(error.message || 'Network error, please try again later');
    }
  };

  /* --------------------------- Assign / Remove --------------------------- */

  const handleRoleChange = useCallback(
    debounce(async (role, action) => {
      let toastMessage = null;
      try {
        const response = await (action == 'assign'
          ? postRoleChangeAPI(role.id)
          : updateApplication(role.id));
        if (response.status === 200) {
          // ✅ Update local assignment state immediately so the toggle/
          // badge reflects the change even before redux syncs
          if (action === 'assign') {
            setAssignedRoleIds((prev) =>
              prev.includes(role.id) ? prev : [...prev, role.id],
            );
          } else {
            setAssignedRoleIds((prev) => prev.filter((id) => id !== role.id));
          }

          // Same pattern as your ComplianceOfficer screen — refresh redux roles
          if (response.data && response.data.roles) {
            dispatch(setAvailableRoleList(response.data.roles));
          }
          toastMessage = {
            message:
              response.message ||
              (action === 'assign'
                ? `${role.label} role assigned`
                : `${role.label} role removed`),
            type: 'success',
          };
        } else {
          toastMessage = { message: response.message, type: 'error' };
        }
      } catch (error) {
        toastMessage = { message: error.message, type: 'error' };
      } finally {
        setLoading(false); // Ensure `loading` is false first
        // Show toast after loading is false
        if (toastMessage) {
          setTimeout(() => {
            showToast(toastMessage.message, toastMessage.type);
          }, 300); // Small delay if needed
        }
      }
    }, 1000),
    [], // Dependencies
  );

  const onToggleRole = (def) => {
    if (isAssigned(def)) {
      // Safety: never allow removing the last remaining role
      if (assignedRoleIds.length <= 1) {
        showToast('You must keep at least one role on your account', 'error');
        return;
      }
      setRoleToRemove(def);
      setShowRemoveModal(true);
      return;
    }
    setLoading(true);
    handleRoleChange(def, 'assign');
  };

  const confirmRemoveRole = () => {
    setShowRemoveModal(false);
    setLoading(true);
    handleRoleChange(roleToRemove, 'remove');
  };

  /* ------------------------------- Filtering ------------------------------ */

  const filteredRoles = ROLE_DEFINITIONS.filter((def) => {
    const q = searchQuery.toLowerCase();
    const matchesSearch =
      def.label.toLowerCase().includes(q) ||
      def.description.toLowerCase().includes(q);
    if (!matchesSearch) return false;
    if (filter === 'assigned') return isAssigned(def);
    if (filter === 'available') return !isAssigned(def);
    return true;
  });

  const renderRoleIcon = (def) => {
    if (def.icon === 'shield') {
      return <ShieldCheck className="w-5 h-5" strokeWidth={1.8} />;
    }
    return <UserRound className="w-5 h-5" strokeWidth={1.8} />;
  };

  /* -------------------------------- Render -------------------------------- */

  return (
    <div className="pt-4 p-2 lg:min-w-[78vw] lg:max-w-[80vw]">
      {loading && (
        <div className="fixed top-0 left-0 w-full h-full flex justify-center items-center bg-black bg-opacity-50 z-[999]">
          <div className="w-12 h-12 border-4 border-white border-t-transparent rounded-full animate-spin"></div>
        </div>
      )}

      <div className="w-full p-4 bg-white rounded-lg shadow-lg relative">
        {/* Header */}
        <div className="flex flex-col lg:flex-row lg:justify-between lg:items-center gap-4 mb-4 border-b pb-5 border-gray-100">
          <div className="flex items-center">
            <span
              className={`material-symbols-outlined mr-2 text-3xl transition-all duration-300 text-black
                    }`}
            >
              admin_panel_settings
            </span>
            <div>
              <span className="font-medium lg:text-xl text-sm">
                Role Permissions
              </span>
              <p className="text-xs text-[#8C8C8C] mt-1 ">
                Assign or remove roles on your own account. Changes take effect
                immediately.
              </p>
            </div>
          </div>

          {/* My current role badges */}
          <div className="flex items-center flex-wrap gap-2">
            <span className="text-sm text-[#8C8C8C] mr-1">Your roles:</span>
            {assignedRoleIds.length > 0 ? (
              myRoles.map((r) => (
                <span
                  key={r.id || r.name}
                  className="text-xs font-medium text-[#34C046] bg-[#34C046] bg-opacity-20 rounded-xl px-3 py-1.5"
                >
                  {roleBadgeLabel(r)}
                </span>
              ))
            ) : (
              <span className="text-xs text-[#8C8C8C]">No roles assigned</span>
            )}
          </div>
        </div>

        {/* Search */}
        <div className="flex justify-between flex-col lg:flex-row flex-wrap mb-4 gap-4">
          <div className="flex items-center">
            <div className="relative">
              <input
                type="text"
                className="p-2 pl-6 w-full sm:w-80 md:w-96 lg:w-[420px] text-sm border rounded-xl"
                placeholder="Search role"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
              />
              <span className="absolute left-2 top-3 text-gray-500">
                <img
                  src="/search-normal.svg"
                  alt="Company Logo"
                  className="mr-2"
                />
              </span>
            </div>
          </div>
        </div>

        {/* Role cards */}
        {filteredRoles.length > 0 ? (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {filteredRoles.map((def) => {
              const assigned = isAssigned(def);
              return (
                <div
                  key={def.id}
                  className={`rounded-xl border p-5 transition-all duration-200 ${
                    assigned
                      ? 'border-[#375AE7]/30 bg-[#375AE7]/[0.03]'
                      : 'border-gray-200 bg-white'
                  }`}
                >
                  {/* Card header */}
                  <div className="flex items-start justify-between">
                    <div className="flex items-center gap-3">
                      <div
                        className={`w-11 h-11 rounded-full flex items-center justify-center ${def.iconClass}`}
                      >
                        {renderRoleIcon(def)}
                      </div>
                      <div>
                        <div className="flex items-center gap-2">
                          <span className="font-medium text-base text-black">
                            {def.label}
                          </span>
                          {assigned && (
                            <span className="text-[#34C046] bg-[#34C046] bg-opacity-20 text-xs font-medium px-2.5 py-1 rounded-full">
                              Assigned
                            </span>
                          )}
                        </div>
                        <p className="text-xs text-[#8C8C8C] mt-0.5">
                          {def.name}
                        </p>
                      </div>
                    </div>
                  </div>

                  {/* Description */}
                  <p className="text-sm text-gray-500 mt-3">
                    {def.description}
                  </p>

                  {/* Permission chips */}
                  <div className="flex flex-wrap gap-2 mt-3">
                    {def.permissions.map((perm) => (
                      <span
                        key={perm}
                        className="text-xs bg-[#F1F5F9] text-[#334155] rounded-full px-2.5 py-1"
                      >
                        {perm}
                      </span>
                    ))}
                  </div>

                  {/* Toggle — assign / remove me */}
                  <div className="flex items-center justify-between mt-5 pt-4 border-t border-gray-100">
                    <span className="text-sm text-gray-600">
                      {assigned
                        ? `You are a ${def.label}`
                        : `Mark me as ${def.label}`}
                    </span>

                    <button
                      onClick={() => onToggleRole(def)}
                      disabled={loading}
                      className={`
                        relative inline-flex h-6 w-11 items-center rounded-full transition
                        ${assigned ? 'bg-[#375AE7]' : 'bg-gray-300'}
                      `}
                    >
                      <span
                        className={`
                          inline-block h-4 w-4 transform rounded-full bg-white transition
                          ${assigned ? 'translate-x-6' : 'translate-x-1'}
                        `}
                      />
                    </button>
                  </div>
                </div>
              );
            })}
          </div>
        ) : (
          /* Empty state */
          <div className="flex flex-col items-center justify-center py-20 px-6 bg-white rounded-2xl text-center">
            <div className="w-16 h-16 flex items-center justify-center rounded-full bg-blue-50 mb-5">
              <UserRoundX
                className="w-7 h-7 text-[#375AE7]"
                strokeWidth={1.8}
              />
            </div>
            <h3 className="text-base font-semibold text-gray-800">
              No roles found
            </h3>
            <p className="text-sm font-normal text-gray-500 mt-2 max-w-md">
              {searchQuery
                ? `No roles found for "${searchQuery}".`
                : 'No roles match the selected filter.'}
            </p>
          </div>
        )}
      </div>

      {/* Remove role confirmation modal */}
      {showRemoveModal && roleToRemove && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 backdrop-blur-sm">
          <div
            className="
              w-full max-w-md rounded-2xl bg-white p-6
              shadow-[0_20px_60px_-15px_rgba(0,0,0,0.25)]
            "
          >
            <div className="w-12 h-12 rounded-full bg-[#FF5050] bg-opacity-10 flex items-center justify-center mb-4">
              <ShieldAlert
                className="w-6 h-6 text-[#FF5050]"
                strokeWidth={1.8}
              />
            </div>

            <h2 className="text-lg font-semibold text-slate-900">
              Remove {roleToRemove.label} role?
            </h2>

            <p className="mt-2 text-sm text-slate-500">
              You are about to remove the{' '}
              <span className="font-medium text-slate-700">
                {roleToRemove.label}
              </span>{' '}
              role from your own account. You will lose access to the screens
              and actions that require this role.
            </p>

            <div className="mt-5 flex justify-end gap-3">
              <button
                onClick={() => setShowRemoveModal(false)}
                className="
                  rounded-full border border-slate-200 px-4 py-2
                  text-sm text-slate-600 hover:bg-slate-100
                "
              >
                Cancel
              </button>

              <button
                onClick={confirmRemoveRole}
                className="
                  rounded-full px-4 py-2 text-sm font-medium text-white
                  bg-[#FF5050] hover:bg-[#e64848] transition-all
                "
              >
                Confirm Remove
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default RolePermission;
